import React, { useState, useEffect, useRef } from 'react';
import { useChatbot } from '../context/chatbot-provider';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../context/cart-provider';
import { useSites } from '../context/sites-provider';
import { useCurrency } from '../context/currency-context';
import { useLanguage } from '../context/language-provider';
import { FaRobot, FaUser, FaArrowRight, FaCheckCircle, FaLightbulb } from 'react-icons/fa';
import { sendMessageToAgent, AVAILABLE_AGENTS } from '../services/chatbotService';
import parse from 'html-react-parser';
import B2BOnboardingRenderer from '../components/Chatbot/ResponseRenderers/B2BOnboardingRenderer';
import TypingIndicator from '../components/Chatbot/TypingIndicator';
import { Logo } from '../components/Logo';
import { homeUrl } from '../services/service.config';

const B2BOnboarding = () => {
  const { addMessage, clearMessages, setIsLoading } = useChatbot();
  const navigate = useNavigate();
  const { cartAccount, syncCart } = useCart();
  const { currentSite } = useSites();
  const { activeCurrency } = useCurrency();
  const { currentLanguage } = useLanguage();
  const [inputMessage, setInputMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const messagesEndRef = useRef(null);

  // Get context data for AI service - same as existing chatbot
  const getContextData = () => {
    return {
      cartId: cartAccount?.id || null,
      siteId: currentSite || null,
      currency: activeCurrency?.code || null,
      language: currentLanguage || null
    };
  };

  // B2B Onboarding steps
  const onboardingSteps = [
    {
      title: "Welcome to B2B Onboarding",
      description: "Let's get your business set up with our platform",
      icon: <FaRobot className="text-2xl" />,
      status: currentStep >= 0 ? 'completed' : 'pending'
    },
    {
      title: "Company Information",
      description: "Tell us about your business",
      icon: <FaUser className="text-2xl" />,
      status: currentStep >= 1 ? 'completed' : currentStep === 0 ? 'current' : 'pending'
    },
    {
      title: "Business Requirements",
      description: "What are your specific needs?",
      icon: <FaLightbulb className="text-2xl" />,
      status: currentStep >= 2 ? 'completed' : currentStep === 1 ? 'current' : 'pending'
    },
    {
      title: "Setup Complete",
      description: "You're all set to start using our B2B platform",
      icon: <FaCheckCircle className="text-2xl" />,
      status: currentStep >= 3 ? 'completed' : currentStep === 2 ? 'current' : 'pending'
    }
  ];

  // Initialize with welcome message
  useEffect(() => {
    const welcomeMessage = {
      id: Date.now(),
      text: "Welcome to our B2B Onboarding Assistant! I'm here to help you get started with our business platform. Let's begin by understanding your company's needs and requirements. What's your company name and what industry are you in?",
      sender: 'bot',
      timestamp: new Date(),
      type: 'welcome'
    };
    setMessages([welcomeMessage]);
    
    // Ensure the welcome message is visible by scrolling to top
    setTimeout(() => {
      const messagesContainer = document.querySelector('[data-messages-container]');
      if (messagesContainer) {
        messagesContainer.scrollTop = 0;
      }
    }, 100);
  }, []);

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsLoadingLocal(true);

    await processMessage(currentMessage);
  };


  const processMessage = async (currentMessage) => {

    try {
      // Call the real AI service with context - same pattern as existing chatbot
      const contextData = getContextData();
      const response = await sendMessageToAgent(currentMessage, 'b2bonboarding', contextData);
      
      const botMessage = {
        id: Date.now() + 1,
        text: response.message,
        sender: 'bot',
        timestamp: new Date(),
        agentId: response.agentId,
        sessionId: response.sessionId,
        type: response.type || 'text',
        data: response.data || null
      };
      
      setMessages(prev => [...prev, botMessage]);
      
      // Update step based on response content
      const responseText = response.message.toLowerCase();
      if (responseText.includes('company') && responseText.includes('verified')) {
        setCurrentStep(1); // Company verification completed
      } else if (responseText.includes('first name') && responseText.includes('last name')) {
        setCurrentStep(2); // Personal details step
      } else if (responseText.includes('account created') || responseText.includes('registration complete')) {
        setCurrentStep(3); // Setup complete
      }
      
    } catch (error) {
      console.error('B2B Onboarding Error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "I apologize, but I'm having trouble processing your request. Please try again or contact our support team for assistance.",
        sender: 'bot',
        timestamp: new Date(),
        type: 'error'
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoadingLocal(false);
    }
  };

  const handleSkipToLogin = () => {
    navigate('/login');
  };

  const handleSkipToSignup = () => {
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Logo and Navigation */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={homeUrl()} className="flex items-center">
              <Logo />
            </Link>
            <div style={{marginLeft: '200px'}}>
              <h1 className="text-2xl font-bold text-gray-900">
                B2B Onboarding Assistant
              </h1>
              <p className="text-sm text-gray-600">
                Let our AI assistant guide you through setting up your business account
              </p>
            </div>
          </div>

        </div>
      </div>

      {/* Chat Interface - Full screen */}
      <div className="bg-white shadow-lg overflow-hidden" style={{height: 'calc(100vh - 100px)'}}>
        {/* Chat Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <FaRobot className="text-white text-lg" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">B2B Onboarding Assistant</h2>
                <p className="text-sm text-gray-500">I'm here to help you get started</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-500">Online</span>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="overflow-y-auto p-6 bg-gray-50" style={{height: 'calc(100vh - 180px)'}} data-messages-container>
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full px-4 py-6">
              <div className="text-center max-w-md">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaRobot className="text-2xl text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Welcome to B2B Onboarding</h3>
                <p className="text-gray-600 mb-6">
                  I'll help you set up your business account. Let's start by learning about your company.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl mx-auto px-4 py-8">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} ${index === 0 ? 'mt-4' : ''}`}
                >
                  <div className={`flex items-start space-x-3 max-w-[85%] ${
                    message.sender === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                  }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      message.sender === 'user' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white border border-gray-200 text-gray-600'
                    }`}>
                      {message.sender === 'user' ? <FaUser className="text-sm" /> : <FaRobot className="text-sm" />}
                    </div>
                    <div className={`rounded-xl shadow-sm ${
                      message.sender === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-200'
                    }`}>
                      <div className={`${message.sender === 'user' ? 'p-3' : 'p-3'}`}>
                            {message.sender === 'user' ? (
                              <div className="text-sm">{message.text}</div>
                            ) : (
                              <B2BOnboardingRenderer message={message} />
                            )}
                      </div>
                      <div className={`px-3 pb-2 text-xs ${
                        message.sender === 'user' ? 'text-blue-200' : 'text-gray-500'
                      }`}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {isLoadingLocal && <TypingIndicator variant="default" />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

            {/* Input Area */}
            <div className="border-t border-gray-200 p-4 bg-white" style={{ zIndex: '999999999 !important' }}>
          <form onSubmit={handleSendMessage} className="w-full">
            <div className="flex space-x-3">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={isLoadingLocal ? "AI is responding..." : "Ask me anything about B2B setup..."}
                className={`flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white ${
                  isLoadingLocal ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoadingLocal}
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoadingLocal}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 font-medium"
              >
                <FaArrowRight className="text-sm" />
                <span>Send</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default B2BOnboarding;
